import type { Modifier } from '@atlaskit/popper';
import { HEADER_HEIGHT } from '../../constants.tsx';
import type { Column } from '../../types.tsx';
import { calculateStickyColumnsWidth } from '../calculate-sticky-columns-width/index.tsx';

const INLINE_EDIT_POPUP_SCROLL_MARGIN = 8;

export const getInlineEditModifier = ({
	columns,
	getScrollInfo,
	isStickyColumnsVisible,
}: {
	isStickyColumnsVisible: boolean;
	columns: Column[];
	getScrollInfo: () => {
		isScrolling: boolean;
		scrollElement: HTMLElement | null;
		isSmallScreen: boolean;
	};
}): (() => Modifier<'closeOnThreshold', object>[]) => {
	const stickyColumnsWidth = calculateStickyColumnsWidth(columns);
	return () => [
		{
			name: 'closeOnThreshold',
			enabled: true,
			phase: 'write',
			// When inline edit popup is open and the table is being scrolled,
			// hide the popup if it moves outside the table’s visible boundary.
			fn: ({ state }) => {
				const { scrollElement, isScrolling, isSmallScreen } = getScrollInfo();
				const totalWidth = isStickyColumnsVisible && !isSmallScreen ? stickyColumnsWidth : 0;

				const scrollRect = scrollElement?.getBoundingClientRect();

				if (!scrollRect) {
					return;
				}
				const referenceElementRect = state.elements.reference.getBoundingClientRect();

				const shoudHide =
					referenceElementRect.left < scrollRect.left + totalWidth ||
					referenceElementRect.top < scrollRect.top + HEADER_HEIGHT ||
					referenceElementRect.bottom > scrollRect.bottom;
				if (shoudHide && isScrolling) {
					// eslint-disable-next-line no-param-reassign
					state.elements.popper.style.display = 'none';
				}
			},
			// When the popup is closed and a field is partially hidden (behind merged columns),
			// auto-scroll the table to bring that field back into view.
			effect: ({ state }) => {
				const { scrollElement, isSmallScreen } = getScrollInfo();
				const totalWidth = isStickyColumnsVisible && !isSmallScreen ? stickyColumnsWidth : 0;

				const scrollRect = scrollElement?.getBoundingClientRect();

				if (!scrollRect) {
					return;
				}
				const referenceElementRect = state.elements.reference.getBoundingClientRect();
				const isPartiallyHidden = referenceElementRect.left < scrollRect.left + totalWidth;

				if (scrollElement && isPartiallyHidden && state.elements.popper.style.display !== 'none') {
					// When clicking a partially hidden field, auto-scrolls it back into the table view
					// and leaves an 8px margin on the left side of the field.
					const scrollAmount =
						scrollRect.left +
						totalWidth -
						referenceElementRect.left +
						INLINE_EDIT_POPUP_SCROLL_MARGIN;

					scrollElement.scrollBy({
						left: -scrollAmount,
						behavior: 'auto',
					});
				}
			},
		},
	];
};
